import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
} from "recharts";

export default function Analytics() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/analytics/summary").then((r) => setStats(r.data)).catch((e) =>
      toast.error(formatApiError(e.response?.data?.detail))
    );
  }, []);

  if (!stats) return <div className="brutal p-6 inline-block">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <header>
        <span className="chip bg-sky">Analytics</span>
        <h1 className="text-4xl md:text-5xl font-extrabold mt-3">Insights</h1>
        <p className="text-muted-foreground font-medium mt-1">Last 14 days of activity.</p>
      </header>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="brutal bg-white p-5 lg:col-span-2">
          <div className="text-xs font-bold uppercase tracking-wider mb-3">Daily DMs</div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={stats.daily}>
              <CartesianGrid strokeDasharray="2 2" stroke="rgba(0,0,0,0.15)" />
              <XAxis dataKey="date" stroke="#000" fontSize={11} />
              <YAxis stroke="#000" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ border: "2px solid black", borderRadius: 6, boxShadow: "4px 4px 0 0 #000" }} />
              <Line type="monotone" dataKey="sent" stroke="hsl(15 100% 50%)" strokeWidth={3} dot={{ r: 4, strokeWidth: 2, fill: "white" }} />
              <Line type="monotone" dataKey="failed" stroke="hsl(0 80% 55%)" strokeWidth={3} dot={{ r: 4, strokeWidth: 2, fill: "white" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="brutal bg-mint p-5">
          <div className="text-xs font-bold uppercase tracking-wider mb-3">Lifetime totals</div>
          <ul className="space-y-3">
            <li className="flex justify-between font-bold"><span>Triggers</span><span className="font-heading text-2xl">{stats.total_triggers}</span></li>
            <li className="flex justify-between font-bold"><span>DMs sent</span><span className="font-heading text-2xl">{stats.dms_sent}</span></li>
            <li className="flex justify-between font-bold"><span>DMs failed</span><span className="font-heading text-2xl">{stats.dms_failed}</span></li>
            <li className="flex justify-between font-bold"><span>Success rate</span><span className="font-heading text-2xl">{stats.success_rate}%</span></li>
            <li className="flex justify-between font-bold"><span>Active rules</span><span className="font-heading text-2xl">{stats.rules_active}/{stats.rules_total}</span></li>
          </ul>
        </div>
      </div>

      <div className="brutal bg-white p-5">
        <div className="text-xs font-bold uppercase tracking-wider mb-3">Top keywords</div>
        {stats.top_keywords.length === 0 ? (
          <div className="text-sm text-muted-foreground font-medium py-6 text-center">No DMs sent yet — your top keywords will appear here.</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.top_keywords}>
              <CartesianGrid strokeDasharray="2 2" stroke="rgba(0,0,0,0.15)" />
              <XAxis dataKey="keyword" stroke="#000" fontSize={11} />
              <YAxis stroke="#000" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ border: "2px solid black", borderRadius: 6, boxShadow: "4px 4px 0 0 #000" }} />
              <Bar dataKey="count" fill="hsl(260 100% 75%)" stroke="#000" strokeWidth={2} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
